import { apiClient } from '@/lib/api';
import type { Patient, CreatePatientRequest, UpdatePatientRequest, PatientTest } from '@/types/patient';
import { useAuthStore } from '@/store';

const BASE_URL = '/api/v1/patients';

export const patientService = {
  /**
   * Search/list patients
   * GET /api/v1/patients?search={query}&labId={labId}
   */
  async searchPatients(searchQuery?: string): Promise<Patient[]> {
    const { user } = useAuthStore.getState();
    const labId = user?.defaultLabId;
    
    const params: Record<string, string> = {};
    if (labId) params.labId = labId;
    if (searchQuery) params.search = searchQuery;
    
    const response = await apiClient.get<{ items: Patient[] }>(BASE_URL, { params });
    return response.data.items;
  },

  /**
   * Get patient details
   * GET /api/v1/patients/{id}
   */
  async getPatient(id: string): Promise<Patient> {
    const response = await apiClient.get<Patient>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Get patient test history
   * GET /api/v1/patients/{id}/tests
   */
  async getPatientTests(id: string): Promise<PatientTest[]> {
    const response = await apiClient.get<{ items: PatientTest[] }>(`${BASE_URL}/${id}/tests`);
    return response.data.items;
  },

  /**
   * Create a new patient
   * POST /api/v1/patients?labId={labId}
   */
  async createPatient(data: CreatePatientRequest): Promise<Patient> {
    const { user } = useAuthStore.getState();
    const labId = user?.defaultLabId;
    
    const params = labId ? { labId } : {};
    const response = await apiClient.post<Patient>(BASE_URL, data, { params });
    return response.data;
  },

  /**
   * Update patient
   * PUT /api/v1/patients/{id}
   */
  async updatePatient(id: string, data: UpdatePatientRequest): Promise<Patient> {
    const response = await apiClient.put<Patient>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Delete patient
   * DELETE /api/v1/patients/{id}
   */
  async deletePatient(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },
};